import React, { useState } from 'react';
import { Autocomplete, TextField, useTheme } from '@mui/material';
import { searchCoursesStyles } from './SearchCourses.styles';

export interface SearchOption {
  id: number | string;
  name: string;
  kind: 'collection' | 'template';
  path: { id: number | string | null; name: string }[];
}

export interface SearchCoursesProps {
  options: SearchOption[];
  onSelect: (option: SearchOption) => void;
  placeholder?: string;
}

export const SearchCourses: React.FC<SearchCoursesProps> = ({
  options,
  onSelect,
  placeholder = 'Search by name',
}) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState('');

  const handleSelect = (_event: React.SyntheticEvent, option: SearchOption | null) => {
    if (option) {
      onSelect(option);
      setSearchValue('');
    }
  };

  const getTopCollectionLabel = (option: SearchOption): string => {
    const pathWithoutLibrary = option.path
      .map((p) => p.name)
      .filter(Boolean)
      .filter((name) => name.toLowerCase() !== 'library');

    return pathWithoutLibrary.length > 0 ? pathWithoutLibrary[0] : '';
  };

  const filterByName = (
    opts: SearchOption[],
    state: { inputValue: string }
  ): SearchOption[] => {
    const templateOptions = opts.filter((opt) => opt.kind === 'template');
    const inputValue = state.inputValue.toLowerCase().trim();

    if (!inputValue) {
      return templateOptions;
    }
    return templateOptions.filter((opt) => opt.name.toLowerCase().includes(inputValue));
  };

  return (
    <Autocomplete<SearchOption, false, false, false>
      options={options}
      getOptionLabel={(option) => option.name}
      groupBy={getTopCollectionLabel}
      filterOptions={filterByName}
      inputValue={searchValue}
      onInputChange={(_event, newValue) => setSearchValue(newValue)}
      onChange={handleSelect}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id && option.kind === value.kind
      }
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <li key={`${option.kind}-${option.id}`} {...restProps}>
            <span className="search-courses-result-name">{option.name}</span>
          </li>
        );
      }}
      sx={searchCoursesStyles.autocomplete(theme)}
      slotProps={{
        paper: {
          sx: searchCoursesStyles.paper(theme),
        },
        listbox: {
          sx: searchCoursesStyles.listbox(theme),
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          size="small"
        />
      )}
    />
  );
};

export default SearchCourses;
